import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import PointConversionsApi from 'api/PointConversionsApi';
import Button from 'components/atoms/ui/Button';
import FlexibleModalContainer from 'containers/FlexibleModalContainer';
import { BUTTON_MAIN_TYPE } from 'constants/ui';
import { IPointsConverion } from 'interfaces/components/wall/Ipoints';
import modalStyle from 'assets/style/components/Modals/Modal.module.scss';
import pointsStyle from 'sass-boilerplate/stylesheets/components/launch/Points.module.scss';

export const isReservedConversion = (conversion: IPointsConverion) =>
  Number(conversion?.status) === 2 && Boolean(conversion?.reconciliationStartedAt) && !conversion?.orderUuid;

export const HuurayResolutionModal = ({ conversion, onClose, onValidateSuccess }: {
  conversion: IPointsConverion | null;
  onClose: () => void;
  onValidateSuccess: (conversion: { id: number; status: number }) => void;
}) => {
  const { formatMessage } = useIntl();
  const api = useMemo(() => new PointConversionsApi(), []);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<'pending' | 'unavailable' | null>(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [actionError, setActionError] = useState(false);

  useEffect(() => {
    setCheckResult(null);
    setDecisionOpen(false);
    setActionError(false);
  }, [conversion?.id]);

  const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';
  const dueAt = conversion?.reconciliationDeadlineAt || null;
  const details = conversion ? [
    [formatMessage({ id: 'wall.pointConversions.name' }), conversion.name],
    [formatMessage({ id: 'wall.pointConversions.email' }), conversion.email],
    [formatMessage({ id: 'wall.pointConversions.platformName' }), conversion.platform?.name],
    [formatMessage({ id: 'wall.pointConversions.programName' }), conversion.program?.name],
    [formatMessage({ id: 'wall.pointConversions.points' }), `${conversion.points} WINS`],
    [formatMessage({ id: 'wall.pointConversions.amount' }), `${conversion.amount} ${conversion.currency || 'EUR'}`],
    [formatMessage({ id: 'wall.pointConversions.transactionRefId' }), conversion.transactionRefId],
    [formatMessage({ id: 'wall.pointConversions.createdAt' }), formatDate(conversion.createdAt)],
    [formatMessage({ id: 'pointsConversions.reconciliation.notifiedAt' }), formatDate(conversion.reconciliationNotifiedAt)],
    [formatMessage({ id: 'pointsConversions.reconciliation.deadline' }), dueAt ? formatDate(dueAt) : formatMessage({ id: 'pointsConversions.reconciliation.notificationPending' })],
    [formatMessage({ id: 'pointsConversions.reconciliation.lastCheckedAt' }), formatDate(conversion.reconciliationLastCheckedAt)]
  ] : [];

  const verify = async () => {
    if (!conversion?.id || checking) return;
    setChecking(true);
    setCheckResult(null);
    setActionError(false);
    try {
      const { outcome } = await api.checkHuurayReconciliation(Number(conversion.id));
      if (outcome === 'done' || outcome === 'released') {
        onValidateSuccess({ id: Number(conversion.id), status: outcome === 'done' ? 4 : 3 });
        toast(formatMessage({ id: outcome === 'done'
          ? 'pointsConversions.reconciliation.confirmed'
          : 'pointsConversions.reconciliation.released' }));
        onClose();
      } else {
        setCheckResult(outcome);
        setDecisionOpen(true);
      }
    } catch (error) {
      setCheckResult('unavailable');
      setDecisionOpen(false);
    } finally {
      setChecking(false);
    }
  };

  const decide = async (decision: 'generated' | 'not_generated') => {
    if (!conversion?.id || checking) return;
    setChecking(true);
    setActionError(false);
    try {
      const { outcome } = await api.resolveHuurayReconciliation(Number(conversion.id), decision);
      onValidateSuccess({ id: Number(conversion.id), status: outcome === 'done' ? 4 : 3 });
      toast(formatMessage({ id: outcome === 'done'
        ? 'pointsConversions.reconciliation.manualConfirmed'
        : 'pointsConversions.reconciliation.released' }));
      setDecisionOpen(false);
      onClose();
    } catch (error) {
      setActionError(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <FlexibleModalContainer
        isModalOpen={Boolean(conversion) && !decisionOpen}
        closeModal={() => !checking && onClose()}
        className={modalStyle.largeModal}
        animationClass={pointsStyle.resolutionModalAnimation}
        fullOnMobile={false}
      >
        {conversion && (
          <div className={pointsStyle.resolutionModal}>
            <h2>{formatMessage({ id: 'pointsConversions.reconciliation.modalTitle' })} #{conversion.id}</h2>
            <p>{formatMessage({ id: 'pointsConversions.reconciliation.reservedHelp' })}</p>
            <dl>
              {details.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt><dd>{value || '—'}</dd>
                </div>
              ))}
            </dl>
            {checkResult === 'unavailable' && <div className={pointsStyle.resolutionFeedback} role="alert">
              {formatMessage({ id: 'pointsConversions.reconciliation.checkUnavailable' })}
            </div>}
            <div className={pointsStyle.resolutionModalActions}>
              <Button onClick={verify} disabled={checking} loading={checking}>
                {formatMessage({ id: 'pointsConversions.reconciliation.verify' })}
              </Button>
              <Button type={BUTTON_MAIN_TYPE.DANGER} onClick={onClose} disabled={checking}>
                {formatMessage({ id: 'pointsConversions.reconciliation.close' })}
              </Button>
            </div>
          </div>
        )}
      </FlexibleModalContainer>
      <FlexibleModalContainer
        isModalOpen={Boolean(conversion) && decisionOpen}
        closeModal={() => !checking && onClose()}
        className={modalStyle.largeModal}
        animationClass={pointsStyle.resolutionModalAnimation}
        fullOnMobile={false}
      >
        {conversion && (
          <div className={pointsStyle.resolutionModal}>
            <h2>{formatMessage({ id: 'pointsConversions.reconciliation.decisionTitle' })} #{conversion.id}</h2>
            <p>{formatMessage({ id: checkResult === 'pending'
              ? (dueAt ? 'pointsConversions.reconciliation.notFoundNext' : 'pointsConversions.reconciliation.notFoundAwaitingDeadline')
              : 'pointsConversions.reconciliation.checkUnavailable' },
              { deadline: dueAt ? formatDate(dueAt) : '—' })}</p>
            <div className={pointsStyle.resolutionFeedback} role="status">
              {formatMessage({ id: 'pointsConversions.reconciliation.decisionHelp' })}
            </div>
            {actionError && <div className={pointsStyle.resolutionFeedback} role="alert">
              {formatMessage({ id: 'pointsConversions.reconciliation.decisionError' })}
            </div>}
            <div className={pointsStyle.resolutionModalActions}>
              <Button type={BUTTON_MAIN_TYPE.SECONDARY} onClick={onClose} disabled={checking}>
                {formatMessage({ id: 'pointsConversions.reconciliation.wait' })}
              </Button>
              <Button type={BUTTON_MAIN_TYPE.DANGER} onClick={() => decide('not_generated')} disabled={checking}>
                {formatMessage({ id: 'pointsConversions.reconciliation.manualAbsent' })}
              </Button>
              <Button onClick={() => decide('generated')} disabled={checking} loading={checking}>
                {formatMessage({ id: 'pointsConversions.reconciliation.manualGenerated' })}
              </Button>
            </div>
          </div>
        )}
      </FlexibleModalContainer>
    </>
  );
};
